/**
 * Score Manager
 *
 * Tracks score, combo, and accuracy statistics
 */

import { Judgment } from './JudgmentSystem';

export interface Statistics {
  score: number;
  combo: number;
  maxCombo: number;
  accuracy: number;
  judgments: {
    perfect: number;
    great: number;
    good: number;
    miss: number;
  };
  totalNotes: number;
}

export class ScoreManager {
  private score: number = 0;
  private combo: number = 0;
  private maxCombo: number = 0;

  private judgmentCounts = {
    perfect: 0,
    great: 0,
    good: 0,
    miss: 0
  };

  private totalNotes: number = 0;

  /**
   * Process a judgment and update score/combo
   */
  addJudgment(judgment: Judgment): void {
    this.totalNotes++;

    // Update judgment counts
    const type = judgment.type.toLowerCase() as keyof typeof this.judgmentCounts;
    this.judgmentCounts[type]++;

    // Update combo
    if (judgment.type !== 'MISS') {
      this.combo++;
      this.maxCombo = Math.max(this.maxCombo, this.combo);
    } else {
      this.combo = 0;
    }

    // Calculate score with combo multiplier
    const multiplier = this.getComboMultiplier();
    const points = Math.floor(judgment.score * multiplier);
    this.score += points;

    // Debug: Log accuracy after each judgment
    const accuracy = this.getAccuracy();
    console.log(`[ScoreManager] ${judgment.type} added. Total: ${this.totalNotes} (P:${this.judgmentCounts.perfect} Gr:${this.judgmentCounts.great} Go:${this.judgmentCounts.good} M:${this.judgmentCounts.miss}) -> Accuracy: ${accuracy.toFixed(2)}%`);
  }

  /**
   * Calculate combo multiplier
   * Every 10 combo adds 0.1x, max 2.0x at 100 combo
   */
  private getComboMultiplier(): number {
    return Math.min(1.0 + (Math.floor(this.combo / 10) * 0.1), 2.0);
  }

  /**
   * Calculate accuracy percentage
   */
  getAccuracy(): number {
    if (this.totalNotes === 0) return 100;

    const weightedSum =
      (this.judgmentCounts.perfect * 1.0) +
      (this.judgmentCounts.great * 0.75) +
      (this.judgmentCounts.good * 0.5) +
      (this.judgmentCounts.miss * 0.0);

    return (weightedSum / this.totalNotes) * 100;
  }

  /**
   * Get current score
   */
  getScore(): number {
    return this.score;
  }

  /**
   * Get current combo
   */
  getCombo(): number {
    return this.combo;
  }

  /**
   * Get max combo achieved
   */
  getMaxCombo(): number {
    return this.maxCombo;
  }

  /**
   * Get all statistics
   */
  getStatistics(): Statistics {
    return {
      score: this.score,
      combo: this.combo,
      maxCombo: this.maxCombo,
      accuracy: this.getAccuracy(),
      judgments: { ...this.judgmentCounts },
      totalNotes: this.totalNotes
    };
  }

  /**
   * Reset all scores and statistics
   */
  reset(): void {
    this.score = 0;
    this.combo = 0;
    this.maxCombo = 0;
    this.judgmentCounts = {
      perfect: 0,
      great: 0,
      good: 0,
      miss: 0
    };
    this.totalNotes = 0;
  }

  /**
   * Get letter grade based on accuracy (lenient grading)
   */
  getGrade(): string {
    const accuracy = this.getAccuracy();

    if (accuracy === 100 && this.judgmentCounts.perfect === this.totalNotes) {
      return 'S+';  // Perfect play - all perfects!
    } else if (accuracy >= 95) {
      return 'S';   // Excellent
    } else if (accuracy >= 85) {
      return 'A';   // Very good
    } else if (accuracy >= 70) {
      return 'B';   // Good
    } else if (accuracy >= 50) {
      return 'C';   // Okay
    } else {
      return 'D';   // Needs improvement
    }
  }
}
